import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import OvSearchField from './OvSearchField';
import { InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { debounce } from 'lodash';

type OvSearchInputProps = {
  placeholder?: string;
  initialValue?: string;
  debounceMs?: number;
  onSearchChange: (value: string) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
};

const DEFAULT_DEBOUNCE_MS = 500;

const OvSearchInput: FC<OvSearchInputProps> = ({
  placeholder,
  initialValue,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onSearchChange,
  fullWidth,
  disabled,
  className,
}) => {
  const [value, setValue] = useState<string>(initialValue ?? '');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const onSearchChangeRef = useRef(onSearchChange);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  const debouncedNotify = useMemo(
    () =>
      debounce(
        (nextValue: string) => onSearchChangeRef.current(nextValue),
        debounceMs
      ),
    [debounceMs]
  );

  useEffect(() => {
    return () => {
      debouncedNotify.cancel();
    };
  }, [debouncedNotify]);

  useEffect(() => {
    if (!isFocused) {
      setValue(initialValue ?? '');
    }
  }, [initialValue, isFocused]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setValue(next);
    debouncedNotify(next);
  };

  const onBlur = () => {
    setIsFocused(false);
    if (typeof (debouncedNotify as any).flush === 'function') {
      (debouncedNotify as any).flush();
    }
  };

  const onClear = () => {
    setValue('');
    onSearchChange('');
  };

  const onFocus = () => setIsFocused(true);

  return (
    <OvSearchField
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      fullWidth={fullWidth}
      className={className}
      InputProps={{
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="clear search"
              onClick={onClear}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : (
          <InputAdornment position="end">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );
};

export default OvSearchInput;
